import { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { usePWAInstallContext } from '@/contexts/PWAInstallContext';
import { toast } from 'sonner';
import { Download, Gift, Smartphone, Share, Plus, X, Sparkles, CheckCircle, PartyPopper } from 'lucide-react';
import { GCBadgeSVG, SCBadgeSVG } from '@/components/CurrencyBadge';

export const PWAInstallModal = () => {
  const {
    showInstallPrompt,
    dismissInstallPrompt,
    isIOS,
    hasNativePrompt,
    promptInstall,
    status,
    claimReward,
    isStandalone,
    markInstalled,
  } = usePWAInstallContext();

  const [installing, setInstalling] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [step, setStep] = useState<'install' | 'claim' | 'success'>('install');

  // Don't show if already running as PWA or reward already claimed
  if (isStandalone || status?.rewardClaimed) {
    return null;
  }

  const handleInstall = async () => {
    if (!hasNativePrompt) {
      return;
    }

    setInstalling(true);
    try {
      const accepted = await promptInstall();
      if (accepted) {
        toast.success('App installed successfully!');
        await markInstalled();
        setStep('claim');
      }
    } catch {
      toast.error('Installation failed. Please try again.');
    } finally {
      setInstalling(false);
    }
  };

  const handleClaimReward = async () => {
    if (claiming) return;
    setClaiming(true);
    try {
      const result = await claimReward();
      if (result) {
        setStep('success');
        toast.success(
          `You received ${result.gcAmount.toLocaleString()} GC and ${result.scAmount} SC!`,
          { duration: 5000 }
        );
      }
    } catch {
      toast.error('Failed to claim reward. Please try again.');
    } finally {
      setClaiming(false);
    }
  };

  const handleClose = () => {
    dismissInstallPrompt();
    setStep('install');
  };

  // If user can claim (already installed), start at claim step
  const currentStep = status?.canClaim ? (step === 'install' ? 'claim' : step) : step;

  // Full-screen dialog class matching JoiningBonusModal pattern
  const fullScreenDialogClass = "!fixed !left-0 !top-0 !right-0 !bottom-0 !w-[100vw] !h-[100vh] !max-w-none !max-h-none lg:!left-[50%] lg:!top-[50%] lg:!right-auto lg:!bottom-auto lg:!w-[95vw] lg:!max-w-md lg:!h-auto lg:!max-h-none m-0 p-0 rounded-none lg:rounded-2xl border-0 lg:border lg:border-amber-500/20 bg-transparent overflow-hidden lg:overflow-visible !translate-x-0 !translate-y-0 lg:!translate-x-[-50%] lg:!translate-y-[-50%] [&>button]:hidden pt-[env(safe-area-inset-top)] lg:pt-0 lg:shadow-[0_0_60px_rgba(251,191,36,0.15),0_25px_50px_rgba(0,0,0,0.5)]";

  return (
    <Dialog open={showInstallPrompt} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent 
        className={fullScreenDialogClass} 
        aria-describedby={undefined}
      >
        {/* Full background wrapper */}
        <div className="w-full h-full lg:h-auto bg-card overflow-hidden lg:overflow-visible relative flex flex-col lg:rounded-2xl">
          
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 lg:top-4 lg:right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white/60 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
          
          {/* Radial glow accents */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/4 w-[600px] h-[600px]">
              <div className="w-full h-full rounded-full bg-gradient-radial from-amber-500/15 via-amber-600/5 to-transparent blur-3xl" />
            </div>
            <div className="absolute bottom-0 right-0 w-[400px] h-[400px]">
              <div className="w-full h-full rounded-full bg-gradient-radial from-purple-500/10 via-purple-600/5 to-transparent blur-3xl" />
            </div>
          </div>

          {/* Content */}
          <div className="relative flex-1 flex flex-col items-center px-5 lg:px-6 pb-[calc(6rem+env(safe-area-inset-bottom))] lg:pb-6 pt-2 lg:pt-6 min-h-0 overflow-y-auto lg:overflow-visible">

            {currentStep === 'install' && (
              <div className="my-auto shrink-0 flex flex-col items-center text-center py-6 lg:py-8 w-full max-w-md space-y-5">
                {/* Hero icon */}
                <div className="relative mb-2">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 flex items-center justify-center shadow-[0_0_40px_rgba(251,191,36,0.4)]">
                    <Download className="h-10 w-10 text-amber-900" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg">
                    <Gift className="h-3.5 w-3.5 text-white" />
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-black bg-gradient-to-r from-amber-200 via-yellow-300 to-amber-200 bg-clip-text text-transparent mb-2">
                    Install Gold Machine
                  </h2>
                  <p className="text-sm text-white/50 max-w-xs mx-auto">
                    Get the best experience with instant access
                  </p>
                </div>

                {/* Reward banner */}
                {status?.enabled && !status.rewardClaimed && (
                  <div className="w-full max-w-xs bg-gradient-to-br from-amber-500/15 via-amber-600/10 to-purple-500/10 backdrop-blur-sm border border-amber-400/20 rounded-2xl p-4 shadow-[0_0_30px_rgba(251,191,36,0.1)]">
                    <p className="text-xs font-bold text-amber-300 uppercase tracking-wider mb-3">Install Bonus</p>
                    <div className="flex items-center justify-center gap-4">
                      <div className="flex flex-col items-center gap-1">
                        <GCBadgeSVG size={32} />
                        <span className="text-lg font-black text-amber-200">{status.gcAmount.toLocaleString()}</span>
                      </div>
                      <div className="w-px h-12 bg-white/10" />
                      <div className="flex flex-col items-center gap-1">
                        <SCBadgeSVG size={32} />
                        <span className="text-lg font-black text-emerald-200">{status.scAmount}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Benefits list */}
                <ul className="space-y-3 text-left w-full max-w-xs">
                  <li className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-amber-500/10">
                      <Smartphone className="h-4 w-4 text-amber-400" />
                    </div>
                    <span className="text-sm text-white/80">Instant access from home screen</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-emerald-500/10">
                      <Gift className="h-4 w-4 text-emerald-400" />
                    </div>
                    <span className="text-sm text-white/80">Push notifications for wins & offers</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-purple-500/10">
                      <Sparkles className="h-4 w-4 text-purple-400" />
                    </div>
                    <span className="text-sm text-white/80">Faster loading & smoother gameplay</span>
                  </li>
                </ul>

                {/* Action area */}
                {isIOS ? (
                  // iOS manual instructions
                  <div className="w-full max-w-xs space-y-4">
                    <p className="text-sm font-semibold text-white/80">To install on iOS:</p>
                    <ol className="space-y-3 text-left">
                      <li className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-amber-900 text-xs font-bold flex items-center justify-center">1</span>
                        <span className="text-sm text-white/70">Tap the <Share className="inline h-4 w-4 text-amber-400" /> Share button in Safari</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-amber-900 text-xs font-bold flex items-center justify-center">2</span>
                        <span className="text-sm text-white/70">Scroll and tap <Plus className="inline h-4 w-4 text-amber-400" /> "Add to Home Screen"</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-amber-900 text-xs font-bold flex items-center justify-center">3</span>
                        <span className="text-sm text-white/70">Tap "Add" to confirm, then open from Home Screen</span>
                      </li>
                    </ol>
                    <p className="text-xs text-white/40 text-center">
                      After installing, open the app from your Home Screen to claim your bonus
                    </p>
                    <Button 
                      variant="outline" 
                      className="w-full bg-white/5 hover:bg-white/10 border-white/10 text-white/80" 
                      onClick={handleClose}
                    >
                      I'll do it later
                    </Button>
                  </div>
                ) : hasNativePrompt ? (
                  // Android/Chrome native prompt
                  <Button
                    className="w-full max-w-xs h-12 text-base font-bold bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-amber-950 shadow-[0_0_30px_rgba(251,191,36,0.4)] border-0 rounded-xl"
                    onClick={handleInstall}
                    disabled={installing}
                  >
                    <Download className="h-5 w-5 mr-2" />
                    {installing ? 'Installing...' : 'Install Now'}
                  </Button>
                ) : (
                  // Fallback
                  <Button 
                    variant="outline" 
                    className="w-full max-w-xs bg-white/5 hover:bg-white/10 border-white/10 text-white/80" 
                    onClick={handleClose}
                  >
                    Maybe Later
                  </Button>
                )}
              </div>
            )}

            {currentStep === 'claim' && (
              <div className="my-auto shrink-0 flex flex-col items-center text-center py-6 lg:py-8 w-full max-w-md">
                {/* Success check icon */}
                <div className="relative mb-4">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center shadow-[0_0_40px_rgba(16,185,129,0.4)]">
                    <CheckCircle className="h-10 w-10 text-white" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg animate-bounce">
                    <Gift className="h-3.5 w-3.5 text-amber-900" />
                  </div>
                </div>

                <h2 className="text-2xl font-black bg-gradient-to-r from-amber-200 via-yellow-300 to-amber-200 bg-clip-text text-transparent mb-2">
                  App Installed!
                </h2>
                <p className="text-sm text-white/50 mb-6 max-w-xs">
                  Thank you for installing Gold Machine. Claim your bonus!
                </p>

                {/* Bonus Display */}
                {status && (
                  <div className="w-full max-w-xs bg-gradient-to-br from-amber-500/15 via-amber-600/10 to-purple-500/10 backdrop-blur-sm border border-amber-400/20 rounded-2xl p-5 mb-6 shadow-[0_0_30px_rgba(251,191,36,0.1)]">
                    <p className="text-xs font-bold text-amber-300 uppercase tracking-wider mb-3">Your Install Bonus</p>
                    <div className="flex items-center justify-center gap-4">
                      <div className="flex flex-col items-center gap-1.5">
                        <GCBadgeSVG size={36} />
                        <span className="text-xl font-black text-amber-200">{status.gcAmount.toLocaleString()}</span>
                        <span className="text-[10px] text-white/40 font-medium uppercase tracking-wider">Gold Coins</span>
                      </div>
                      <div className="w-px h-14 bg-white/10" />
                      <div className="flex flex-col items-center gap-1.5">
                        <SCBadgeSVG size={36} />
                        <span className="text-xl font-black text-emerald-200">{status.scAmount}</span>
                        <span className="text-[10px] text-white/40 font-medium uppercase tracking-wider">Sweep Coins</span>
                      </div>
                    </div>
                  </div>
                )}

                <Button
                  onClick={handleClaimReward}
                  className="w-full max-w-xs h-12 text-base font-bold bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-amber-950 shadow-[0_0_30px_rgba(251,191,36,0.4)] border-0 rounded-xl"
                  disabled={claiming}
                >
                  <Gift className="h-5 w-5 mr-2" />
                  {claiming ? 'Claiming...' : 'Claim My Bonus'}
                </Button>
              </div>
            )}

            {currentStep === 'success' && (
              <div className="my-auto shrink-0 flex flex-col items-center text-center py-6 lg:py-8 w-full max-w-md">
                {/* Celebration icon */}
                <div className="relative mb-4">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 flex items-center justify-center shadow-[0_0_60px_rgba(251,191,36,0.5)] animate-pulse">
                    <PartyPopper className="h-12 w-12 text-amber-900" />
                  </div>
                  {/* Sparkle accents */}
                  <Sparkles className="absolute -top-2 -left-2 h-6 w-6 text-amber-300 animate-pulse" style={{ animationDelay: '200ms' }} />
                  <Sparkles className="absolute -top-1 -right-3 h-5 w-5 text-amber-400 animate-pulse" style={{ animationDelay: '500ms' }} />
                  <Sparkles className="absolute -bottom-1 -left-3 h-5 w-5 text-emerald-400 animate-pulse" style={{ animationDelay: '800ms' }} />
                </div>

                <h2 className="text-2xl font-black bg-gradient-to-r from-amber-200 via-yellow-300 to-amber-200 bg-clip-text text-transparent mb-2">
                  Congratulations!
                </h2>
                <p className="text-sm text-white/50 mb-6 max-w-xs">
                  Your install bonus has been added to your account!
                </p>

                {/* Bonus Amounts */}
                {status && (
                  <div className="grid grid-cols-2 gap-3 mb-6 max-w-xs mx-auto">
                    <div className="flex items-center gap-2 bg-amber-500/15 backdrop-blur-sm border border-amber-400/30 rounded-xl px-3 py-3">
                      <GCBadgeSVG size={26} />
                      <span className="text-lg font-black text-amber-200">+{status.gcAmount.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-2 bg-emerald-500/15 backdrop-blur-sm border border-emerald-400/30 rounded-xl px-3 py-3">
                      <SCBadgeSVG size={26} />
                      <span className="text-lg font-black text-emerald-200">+{status.scAmount}</span>
                    </div>
                  </div>
                )}

                <p className="text-xs text-white/40 mb-6">
                  Enjoy your bonus coins!
                </p>

                <Button
                  onClick={handleClose}
                  className="w-full max-w-xs h-12 text-base font-bold bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-amber-950 shadow-[0_0_30px_rgba(251,191,36,0.4)] border-0 rounded-xl"
                >
                  <Sparkles className="h-5 w-5 mr-2" />
                  Start Playing
                </Button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
